import type { Outing } from '../types';
import {
  calculateBookingPricing,
  type BookingSelection,
  type PricingConfig,
} from './pricingEngine';

export type BookingFormRateOption = {
  rateId: string;
  label: string;
  unitPrice: number;
  isFree: boolean;
};

export type BookingFormPricingResult = {
  usesPricingConfig: boolean;
  participantsCount: number;
  totalAmount: number;
  pricingSelection?: BookingSelection[];
  rateOptions: BookingFormRateOption[];
};

function normalizeQuantity(value: unknown): number {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return Math.max(0, Math.trunc(numericValue));
}

function getRateOptions(pricingConfig: PricingConfig): BookingFormRateOption[] {
  return [
    {
      rateId: pricingConfig.normalRate.rateId,
      label: pricingConfig.normalRate.label,
      unitPrice: pricingConfig.normalRate.unitPrice,
      isFree: false,
    },
    ...(pricingConfig.freeChildRule.enabled
      ? [{
          rateId: pricingConfig.freeChildRule.rateId,
          label: pricingConfig.freeChildRule.label,
          unitPrice: pricingConfig.freeChildRule.unitPrice,
          isFree: true,
        }]
      : []),
    ...pricingConfig.specialRates
      .filter((rate) => rate.enabled)
      .map((rate) => ({
        rateId: rate.rateId,
        label: rate.label,
        unitPrice: rate.unitPrice,
        isFree: rate.unitPrice === 0,
      })),
  ];
}

export function createInitialBookingRateQuantities(pricingConfig?: PricingConfig): Record<string, number> {
  if (!pricingConfig) {
    return {};
  }

  return {
    [pricingConfig.normalRate.rateId]: 1,
  };
}

export function getBookingFormPricing({
  outing,
  rateQuantities,
}: {
  outing: Pick<Outing, 'pricingConfig'>;
  participantsCount: number;
  rateQuantities?: Record<string, number>;
}): BookingFormPricingResult {
  if (!outing.pricingConfig) {
    return {
      usesPricingConfig: false,
      participantsCount: 0,
      totalAmount: 0,
      rateOptions: [],
    };
  }

  const rateOptions = getRateOptions(outing.pricingConfig);
  const pricingSelection = rateOptions
    .map((rate) => ({
      rateId: rate.rateId,
      quantity: normalizeQuantity(rateQuantities?.[rate.rateId]),
    }))
    .filter((line) => line.quantity > 0);

  if (pricingSelection.length === 0) {
    return {
      usesPricingConfig: true,
      participantsCount: 0,
      totalAmount: 0,
      pricingSelection,
      rateOptions,
    };
  }

  const pricingResult = calculateBookingPricing(outing.pricingConfig, pricingSelection);

  return {
    usesPricingConfig: true,
    participantsCount: pricingResult.participantTotals.initialParticipants,
    totalAmount: pricingResult.moneyTotals.initialTotal,
    pricingSelection,
    rateOptions,
  };
}

export function requiresPaymentStep({
  paymentMethod,
  totalAmount,
}: {
  paymentMethod: 'mollie' | 'bank_transfer' | 'on_site';
  totalAmount: number;
}): boolean {
  return totalAmount > 0 && paymentMethod === 'mollie';
}